<template>
  <div class="narrative-climate-wrapper">
    <div class="page-header-row">
      <h2 class="page-title">
        Strategic Reputation & Positioning
      </h2>

      <div class="header-controls">
        <div class="header-select-wrapper">
          <select v-model="selectedGroupAnalysisKey" class="header-group-select">
            <option value="" disabled hidden>
              Select Group Analysis
            </option>
            <option
              v-for="(item, index) in itemGroupAnalysis"
              :key="index"
              :value="item.GROUP_ANALYSIS"
            >
              {{ item.GROUP_ANALYSIS }}
            </option>
          </select>
          <v-icon class="header-chevron-icon" size="20">
            mdi-chevron-down
          </v-icon>
        </div>

        <div class="header-select-wrapper header-select-wrapper--period">
          <select v-model="selectedPeriod" class="header-group-select">
            <option
              v-for="opt in periodOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
          <v-icon class="header-chevron-icon" size="20">
            mdi-chevron-down
          </v-icon>
        </div>

        <v-btn
          class="header-apply-btn"
          depressed
          :disabled="isLoadingPublicRelationData"
          @click="loadAllPublicRelationData()"
        >
          <v-icon color="white" size="22" left>
            {{ isLoadingPublicRelationData ? 'mdi-loading mdi-spin' : 'mdi-magnify' }}
          </v-icon>
          {{ isLoadingPublicRelationData ? 'Loading...' : 'Apply' }}
        </v-btn>

        <v-btn
          class="header-setting-btn"
          depressed
          @click="settingDialog = true"
        >
          <v-icon color="white" size="22" left>
            mdi-cog-outline
          </v-icon>
          Brand Config
        </v-btn>
      </div>
    </div>

    <div class="audit-forecast-row">
      <div class="pr-card audit-card">
        <div class="card-header-text">
          <h3 class="card-title">
            MESSAGE PENETRATION AUDIT
          </h3>
          <p class="card-subtitle">
            Our Voice vs. Public Echo
          </p>
        </div>

        <div class="audit-body">
          <div class="col-head col-head--left">
            <p class="col-head-title">
              WHAT WE SENT
            </p>
            <p class="col-head-sub">
              (Active Campaign Pillars)
            </p>
          </div>
          <div class="col-head col-head--right">
            <p class="col-head-title">
              WHAT THEY HEARD
            </p>
            <p class="col-head-sub">
              (Media &amp; Public Echo)
            </p>
          </div>

          <div class="audit-divider" aria-hidden="true"></div>

          <div class="pillar-list" ref="pillarList">
            <div
              v-for="(pillar, index) in pillars"
              :key="index"
              class="pillar-card"
              :class="{
                'pillar-card--active': hoveredPillar === index,
                'pillar-card--dim': hoveredPillar !== null && hoveredPillar !== index
              }"
              tabindex="0"
              @mouseenter="setHover(index)"
              @mouseleave="clearHover"
              @focus="setHover(index)"
              @blur="clearHover"
            >
              <p class="pillar-name">
                {{ pillar.name }}
              </p>
              <div class="pillar-bar-row">
                <div class="pillar-track">
                  <div
                    class="pillar-fill"
                    :style="{ width: pillar.score + '%' }"
                  ></div>
                </div>
                <span class="pillar-score">
                  {{ pillar.score }}%
                </span>
              </div>
            </div>
          </div>

          <svg
            class="audit-connectors"
            ref="connectorsEl"
            :viewBox="'0 0 80 ' + auditHeight"
            :style="{ height: auditHeight + 'px' }"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path
              v-for="line in activeLines"
              :key="line.key"
              class="connector-line"
              :d="line.d"
              fill="none"
              stroke="#22c55e"
              stroke-linecap="round"
              :stroke-width="line.width"
              :stroke-opacity="line.opacity"
            />
          </svg>

          <div class="echo-column" ref="echoColumn">
            <div class="echo-top" ref="echoTopEl">
              <div
                v-for="(row, rIndex) in echoTop"
                :key="'t' + rIndex"
                class="echo-row"
              >
                <span
                  v-for="word in row"
                  :key="word.key"
                  class="echo-word"
                  :data-key="word.key"
                  :class="wordClass(word)"
                  :style="wordStyle(word)"
                >
                  {{ word.text }}
                </span>
              </div>
            </div>

            <div
              class="echo-highlight"
              ref="echoHighlightEl"
              :data-key="echoHighlightKey"
              :class="wordClass(echoHighlightWord)"
            >
              <span
                v-for="(word, index) in echoHighlight.split(' ')"
                :key="index"
                class="echo-highlight-line"
              >
                {{ word }}
              </span>
            </div>

            <div class="echo-bottom">
              <div
                v-for="(row, rIndex) in echoBottom"
                :key="'b' + rIndex"
                class="echo-row"
              >
                <span
                  v-for="word in row"
                  :key="word.key"
                  class="echo-word"
                  :data-key="word.key"
                  :class="wordClass(word)"
                  :style="wordStyle(word)"
                >
                  {{ word.text }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="match-row">
          <svg viewBox="0 0 120 74" class="match-gauge" aria-hidden="true">
            <defs>
              <linearGradient id="pr2-match-green" x1="0" y1="1" x2="1" y2="0">
                <stop offset="0%" stop-color="#3f9d5b" />
                <stop offset="100%" stop-color="#7cc590" />
              </linearGradient>
            </defs>
            <path
              d="M 14 64 A 46 46 0 0 1 106 64"
              fill="none"
              stroke="#d7dde3"
              stroke-width="20"
              pathLength="100"
            />
            <path
              d="M 14 64 A 46 46 0 0 1 106 64"
              fill="none"
              stroke="url(#pr2-match-green)"
              stroke-width="20"
              pathLength="100"
              :stroke-dasharray="campaignMatch + ' 100'"
            />
            <path
              d="M 26 64 A 34 34 0 0 1 94 64"
              fill="none"
              stroke="#ffffff"
              stroke-opacity="0.55"
              stroke-width="1.5"
            />
            <g :transform="'rotate(' + needleAngle + ' 60 64)'">
              <polygon points="56.5,64 60,18 63.5,64" fill="#1e293b" />
              <circle cx="60" cy="64" r="8" fill="#1e293b" />
              <circle cx="60" cy="64" r="3" fill="#e2e8f0" />
            </g>
          </svg>

          <p class="match-text">
            <span>Gauge:</span>
            <span>Overall Campaign</span>
            <span>Match: {{ campaignMatch }}%</span>
          </p>

          <svg
            class="match-arrow"
            :class="{ 'match-arrow--down': campaignTrend === 'down' }"
            viewBox="0 0 32 32"
            width="34"
            height="34"
            fill="none"
            stroke="currentColor"
            stroke-width="4"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <line x1="7" y1="25" x2="24" y2="8" />
            <polyline points="11,8 24,8 24,21" />
          </svg>
        </div>
      </div>

      <div class="pr-card forecast-card">
        <div class="card-header-text">
          <h3 class="card-title">
            NARRATIVE WEATHER FORECAST
          </h3>
          <p class="card-subtitle">
            Reputation Risk Map
          </p>
        </div>

        <div class="forecast-list">
          <div
            v-for="(item, index) in forecasts"
            :key="index"
            class="forecast-item"
            :class="'forecast-item--' + item.type"
          >
            <div class="forecast-icon">
              <svg v-if="item.type === 'clear'" viewBox="0 0 60 50" width="56" height="46" xmlns="http://www.w3.org/2000/svg">
                <circle cx="30" cy="25" r="11" fill="#FDD835" />
                <line x1="30" y1="7" x2="30" y2="2" stroke="#FDD835" stroke-width="2.5" stroke-linecap="round" />
                <line x1="30" y1="43" x2="30" y2="48" stroke="#FDD835" stroke-width="2.5" stroke-linecap="round" />
                <line x1="12" y1="25" x2="7" y2="25" stroke="#FDD835" stroke-width="2.5" stroke-linecap="round" />
                <line x1="48" y1="25" x2="53" y2="25" stroke="#FDD835" stroke-width="2.5" stroke-linecap="round" />
                <line x1="17" y1="12" x2="13" y2="8" stroke="#FDD835" stroke-width="2.5" stroke-linecap="round" />
                <line x1="43" y1="38" x2="47" y2="42" stroke="#FDD835" stroke-width="2.5" stroke-linecap="round" />
                <line x1="43" y1="12" x2="47" y2="8" stroke="#FDD835" stroke-width="2.5" stroke-linecap="round" />
                <line x1="17" y1="38" x2="13" y2="42" stroke="#FDD835" stroke-width="2.5" stroke-linecap="round" />
              </svg>
              <svg v-else-if="item.type === 'overcast'" viewBox="0 0 60 44" width="56" height="42" xmlns="http://www.w3.org/2000/svg">
                <ellipse cx="30" cy="18" rx="18" ry="11" fill="#b0bec5" />
                <ellipse cx="18" cy="24" rx="10" ry="8" fill="#b0bec5" />
                <ellipse cx="42" cy="24" rx="10" ry="8" fill="#b0bec5" />
                <rect x="8" y="22" width="44" height="10" rx="5" fill="#b0bec5" />
              </svg>
              <svg v-else viewBox="0 0 60 50" width="56" height="46" xmlns="http://www.w3.org/2000/svg">
                <ellipse cx="30" cy="14" rx="18" ry="10" fill="#90a4ae" />
                <ellipse cx="18" cy="19" rx="10" ry="8" fill="#90a4ae" />
                <ellipse cx="42" cy="19" rx="10" ry="8" fill="#90a4ae" />
                <rect x="8" y="17" width="44" height="10" rx="5" fill="#90a4ae" />
                <polygon points="32,26 24,38 30,38 27,48 38,34 32,34 35,26" fill="#FDD835" />
                <line x1="15" y1="31" x2="12" y2="40" stroke="#78909c" stroke-width="2" stroke-linecap="round" />
                <line x1="47" y1="31" x2="44" y2="40" stroke="#78909c" stroke-width="2" stroke-linecap="round" />
              </svg>
            </div>

            <div class="forecast-body">
              <p class="forecast-title">
                {{ item.label }}
                <span class="forecast-icon forecast-icon--inline" aria-hidden="true">
                  <svg v-if="item.type === 'clear'" viewBox="0 0 60 50" width="56" height="46" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="30" cy="25" r="11" fill="#FDD835" />
                    <line x1="30" y1="7" x2="30" y2="2" stroke="#FDD835" stroke-width="2.5" stroke-linecap="round" />
                    <line x1="30" y1="43" x2="30" y2="48" stroke="#FDD835" stroke-width="2.5" stroke-linecap="round" />
                    <line x1="12" y1="25" x2="7" y2="25" stroke="#FDD835" stroke-width="2.5" stroke-linecap="round" />
                    <line x1="48" y1="25" x2="53" y2="25" stroke="#FDD835" stroke-width="2.5" stroke-linecap="round" />
                    <line x1="17" y1="12" x2="13" y2="8" stroke="#FDD835" stroke-width="2.5" stroke-linecap="round" />
                    <line x1="43" y1="38" x2="47" y2="42" stroke="#FDD835" stroke-width="2.5" stroke-linecap="round" />
                    <line x1="43" y1="12" x2="47" y2="8" stroke="#FDD835" stroke-width="2.5" stroke-linecap="round" />
                    <line x1="17" y1="38" x2="13" y2="42" stroke="#FDD835" stroke-width="2.5" stroke-linecap="round" />
                  </svg>
                  <svg v-else-if="item.type === 'overcast'" viewBox="0 0 60 44" width="56" height="42" xmlns="http://www.w3.org/2000/svg">
                    <ellipse cx="30" cy="18" rx="18" ry="11" fill="#b0bec5" />
                    <ellipse cx="18" cy="24" rx="10" ry="8" fill="#b0bec5" />
                    <ellipse cx="42" cy="24" rx="10" ry="8" fill="#b0bec5" />
                    <rect x="8" y="22" width="44" height="10" rx="5" fill="#b0bec5" />
                  </svg>
                  <svg v-else viewBox="0 0 60 50" width="56" height="46" xmlns="http://www.w3.org/2000/svg">
                    <ellipse cx="30" cy="14" rx="18" ry="10" fill="#90a4ae" />
                    <ellipse cx="18" cy="19" rx="10" ry="8" fill="#90a4ae" />
                    <ellipse cx="42" cy="19" rx="10" ry="8" fill="#90a4ae" />
                    <rect x="8" y="17" width="44" height="10" rx="5" fill="#90a4ae" />
                    <polygon points="32,26 24,38 30,38 27,48 38,34 32,34 35,26" fill="#FDD835" />
                    <line x1="15" y1="31" x2="12" y2="40" stroke="#78909c" stroke-width="2" stroke-linecap="round" />
                    <line x1="47" y1="31" x2="44" y2="40" stroke="#78909c" stroke-width="2" stroke-linecap="round" />
                  </svg>
                </span>
                <span class="forecast-theme">
                  {{ item.theme }}
                </span>
              </p>
              <p class="forecast-description">
                {{ item.description }}
              </p>
              <p class="forecast-note">
                <strong>Strategy:</strong> {{ item.aiNote }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="pr-card timeline-card">
      <div class="timeline-head">
        <div class="card-header-text">
          <h3 class="card-title">
            NARRATIVE EVENT TIMELINE
          </h3>
          <p class="card-subtitle">
            Key Headlines, {{ timelineRangeLabel }}
          </p>
        </div>
        <div class="timeline-legend">
          <span class="legend-item"><span class="legend-swatch legend-swatch--positive"></span>Positive</span>
          <span class="legend-item"><span class="legend-swatch legend-swatch--neutral"></span>Neutral</span>
          <span class="legend-item"><span class="legend-swatch legend-swatch--negative"></span>Negative</span>
        </div>
      </div>

      <div class="timeline-scroll">
        <div class="timeline-chart" ref="timelineChart">
          <div class="event-band">
            <div
              v-for="(ev, index) in eventMarkers"
              :key="index"
              class="event-label event-label--clickable"
              :class="{ 'event-label--high': ev.tier === 1 }"
              :style="{ left: ev.leftPct + '%' }"
              @click="openEventModal(ev)"
            >
              {{ ev.text }}
            </div>
          </div>

          <svg
            ref="timelineSvg"
            viewBox="0 0 1000 260"
            class="timeline-svg"
            role="img"
            aria-label="Narrative event timeline"
            @mousemove="onChartMove"
            @mouseleave="onChartLeave"
          >
            <g v-for="tick in yTicks" :key="'y' + tick">
              <line
                :x1="chart.left"
                :x2="chart.right"
                :y1="yAtCount(tick)"
                :y2="yAtCount(tick)"
                stroke="#e2e8f0"
                stroke-dasharray="3 5"
              />
              <text
                :x="chart.left - 12"
                :y="yAtCount(tick) + 4"
                text-anchor="end"
                class="axis-text"
              >
                {{ tick }}
              </text>
            </g>

            <line
              v-for="tick in axisTicks"
              :key="'v' + tick.index"
              :x1="tick.x"
              :x2="tick.x"
              :y1="chart.top"
              :y2="chart.baseline"
              stroke="#e2e8f0"
              stroke-dasharray="3 5"
            />

            <defs>
              <linearGradient
                v-for="layer in areaLayers"
                :key="'grad-' + layer.key"
                :id="'pr2-area-' + layer.key"
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="0%" :stop-color="layer.color" stop-opacity="0.6" />
                <stop offset="100%" :stop-color="layer.color" stop-opacity="0.15" />
              </linearGradient>
            </defs>
            <path
              v-for="layer in areaLayers"
              :key="'area-' + layer.key"
              :d="layer.area"
              :fill="'url(#pr2-area-' + layer.key + ')'"
            />
            <path
              v-for="layer in areaLayers"
              :key="'line-' + layer.key"
              :d="layer.line"
              fill="none"
              :stroke="layer.color"
              stroke-width="2"
              stroke-linejoin="round"
            />

            <line :x1="chart.left" :x2="chart.right" :y1="chart.baseline" :y2="chart.baseline" stroke="#cbd5e1" />

            <g
              v-for="(ev, index) in eventMarkers"
              :key="'e' + index"
              class="event-dot"
              @click="openEventModal(ev)"
              @mouseenter="hoverOnEvent = true"
              @mouseleave="hoverOnEvent = false"
            >
              <line :x1="ev.x" :x2="ev.x" y1="0" :y2="ev.y" stroke="#94a3b8" stroke-opacity="0.7" stroke-dasharray="3 4" />
              <circle :cx="ev.x" :cy="ev.y" r="14" fill="transparent" />
              <circle class="event-dot-point" :cx="ev.x" :cy="ev.y" r="5.5" fill="#ffffff" stroke="#0f172a" stroke-width="2.5" />
            </g>

            <g v-if="hoverInfo" class="hover-layer" pointer-events="none">
              <line
                :x1="hoverInfo.x"
                :x2="hoverInfo.x"
                :y1="chart.top"
                :y2="chart.baseline"
                stroke="#94a3b8"
                stroke-width="1.5"
                stroke-dasharray="3 4"
              />
              <circle
                v-for="row in hoverMarkerRows"
                :key="'hm-' + row.key"
                :cx="hoverInfo.x"
                :cy="row.y"
                r="5.5"
                :fill="row.color"
                stroke="#ffffff"
                stroke-width="2"
              />
            </g>

            <text
              v-for="tick in axisTicks"
              :key="'a' + tick.index"
              :x="tick.x"
              :y="chart.baseline + 22"
              text-anchor="middle"
              class="axis-text"
            >
              {{ tick.label }}
            </text>
          </svg>

          <div
            v-if="hoverInfo"
            class="chart-tooltip"
            :class="{ 'chart-tooltip--left': chartTip.flip }"
            :style="{ left: chartTip.left + 'px', top: chartTip.top + 'px' }"
          >
            <div
              v-for="row in hoverInfo.rows"
              :key="'tt-' + row.key"
              class="chart-tooltip-row"
            >
              <span class="chart-tooltip-dot" :style="{ background: row.color }"></span>
              <span class="chart-tooltip-label">{{ row.label }}:</span>
              <strong class="chart-tooltip-value">{{ row.value }}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>

    <transition name="modal-fade">
      <div
        v-if="selectedEvent"
        class="portal-modal-overlay"
        @click.self="closeEventModal"
      >
        <div class="portal-modal">
          <button class="portal-modal-close" @click="closeEventModal" aria-label="Close">
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>

          <div class="portal-modal-header">
            <div>
              <h4 class="portal-modal-title">
                {{ selectedEvent.title }}
              </h4>
              <div class="portal-modal-meta">
                <span class="portal-modal-subtitle">
                  Date: {{ selectedEvent.date }}
                </span>
                <span v-if="selectedEvent.sentiment" class="portal-modal-sentiment">
                  <span class="sentiment-item">
                    <span class="sentiment-dot sentiment-dot--positive"></span>Positive: {{ selectedEvent.sentiment.positive }}
                  </span>
                  <span class="sentiment-item">
                    <span class="sentiment-dot sentiment-dot--neutral"></span>Neutral: {{ selectedEvent.sentiment.neutral }}
                  </span>
                  <span class="sentiment-item">
                    <span class="sentiment-dot sentiment-dot--negative"></span>Negative: {{ selectedEvent.sentiment.negative }}
                  </span>
                </span>
              </div>
            </div>
          </div>

          <div class="portal-modal-body">
            <div class="portal-modal-block">
              <p>{{ selectedEvent.insight || '-' }}</p>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <div>
      <Notification ref="alert"></Notification>
    </div>

    <SettingModal v-model="settingDialog" />
  </div>
</template>

<script>
import axios from 'axios';
import { trackEvent } from '@/components/utils/utils.js';
import Notification from '@/components/Notification';
import { generateFilterDate } from '@/components/utils/utils.js';
import SettingModal from '@/views/BrandAnalysis/components/SettingModal.vue';

const monthsId = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
const maxUnassignedRisks = 10;
const maxPairWordLength = 18;
const maxRowLength = 30;

export default {
  name: 'Strategic Reputation & Positioning',
  components: {
    Notification: Notification,
    SettingModal: SettingModal,
  },
  data() {
    return {
      selectedGroupAnalysisKey: '',
      itemGroupAnalysis: [],
      isLoadingPublicRelationData: false,
      selectedPeriod: 'THIS_WEEK',
      periodOptions: [
        { value: 'TODAY', label: 'Today' },
        { value: 'FROM_YESTERDAY', label: 'From Yesterday' },
        { value: 'THIS_WEEK', label: 'This Week' },
        { value: 'PREVIOUS_WEEK', label: 'From Previous Week' },
        { value: 'THIS_MONTH', label: 'This Month' },
        { value: 'PREVIOUS_MONTH', label: 'Previous Month' }
      ],
      settingDialog: false,
      messagePenetration: null,
      pillars: [],
      echoTop: [],
      echoHighlight: '',
      echoHighlightKey: '',
      echoHighlightWord: null,
      echoBottom: [],
      campaignMatch: 0,
      campaignTrend: 'up',
      auditHeight: 252,
      hoveredPillar: null,
      pillarCenters: [],
      wordTargets: [],
      forecasts: [],
      timelineRange: { start: '', end: '' },
      chart: { left: 70, right: 930, top: 20, baseline: 224 },
      timelineSummary: [],
      events: [],
      selectedEvent: null,
      hoverDay: null,
      chartTip: { left: 0, top: 0, flip: false },
      hoverOnEvent: false
    }
  },
  computed: {
    needleAngle() {
      const value = Math.min(100, Math.max(0, Number(this.campaignMatch) || 0))
      return -90 + value * 1.8
    },
    allEchoWords() {
      const list = [...this.echoTop, ...this.echoBottom].flat()
      return this.echoHighlightWord ? [this.echoHighlightWord, ...list] : list
    },
    echoCountRange() {
      const counts = [...this.echoTop, ...this.echoBottom].flat().map(w => Number(w.count) || 0)
      if (!counts.length) return null
      return { min: Math.min(...counts), max: Math.max(...counts) }
    },
    activeLines() {
      const idx = this.hoveredPillar
      if (idx === null || !this.pillars[idx]) return []
      const score = this.pillars[idx].score
      return this.wordTargets
        .filter(t => t.related.includes(idx))
        .map(t => ({
          key: idx + '-' + t.key,
          d: this.connectorPath(idx, t),
          width: 1.5 + score / 40,
          opacity: 0.6 + score / 250
        }))
    },
    timelineDays() {
      const days = []
      const start = this.parseDate(this.timelineRange.start)
      const end = this.parseDate(this.timelineRange.end)
      if (start === null || end === null || start > end) return days
      for (let t = start; t <= end; t += 86400000) days.push(t)
      return days
    },
    axisTicks() {
      const n = this.timelineDays.length
      if (!n) return []
      const step = Math.max(1, Math.ceil(n / 8))
      const idx = []
      for (let i = 0; i < n; i += step) idx.push(i)
      const last = idx[idx.length - 1]
      if (last !== n - 1) {
        if ((n - 1 - last) * 2 >= step) idx.push(n - 1)
        else idx[idx.length - 1] = n - 1
      }
      return idx.map(i => ({
        index: i,
        x: this.xAt(i),
        label: this.formatDay(this.timelineDays[i])
      }))
    },
    timelineRangeLabel() {
      const days = this.timelineDays
      if (!days.length) return ''
      const year = new Date(days[days.length - 1]).getUTCFullYear()
      return `${this.formatDay(days[0])} - ${this.formatDay(days[days.length - 1])} ${year}`
    },
    dailySeries() {
      const byIndex = {}
      this.timelineSummary.forEach(item => {
        const idx = this.dayIndex(item.date)
        if (idx < 0) return
        const cur = byIndex[idx] || { positive: 0, neutral: 0, negative: 0 }
        cur.positive += Number(item.positive_sentiment) || 0
        cur.neutral += Number(item.neutral_sentiment) || 0
        cur.negative += Number(item.negative_sentiment) || 0
        byIndex[idx] = cur
      })
      return this.timelineDays.map((_, i) => ({
        index: i,
        positive: byIndex[i] ? byIndex[i].positive : 0,
        neutral: byIndex[i] ? byIndex[i].neutral : 0,
        negative: byIndex[i] ? byIndex[i].negative : 0
      }))
    },
    timelineYMax() {
      const totals = this.dailySeries.map(d => d.positive + d.neutral + d.negative)
      const max = Math.max(0, ...totals)
      if (!max) return 10
      const step = max <= 10 ? 2 : max <= 50 ? 10 : max <= 100 ? 20 : 50
      return Math.ceil(max / step) * step
    },
    yTicks() {
      const max = this.timelineYMax
      return [0, max / 2, max]
    },
    areaLayers() {
      const s = this.dailySeries
      if (s.length < 2) return []
      const points = pick =>
        s.map((d, i) => ({ x: this.xAt(i), y: this.yAtCount(pick(d)) }))

      const band = (key, color, upper, lower) => {
        const line = this.monotonePath(upper)
        const back = this.monotonePath([...lower].reverse()).replace(/^M/, 'L')
        return { key, color, line, area: `${line} ${back} Z` }
      }

      const zero = points(() => 0)
      const pos = points(d => d.positive)
      const neu = points(d => d.positive + d.neutral)
      const neg = points(d => d.positive + d.neutral + d.negative)

      return [
        band('negative', '#FF4560', neg, neu),
        band('neutral', '#008FFB', neu, pos),
        band('positive', '#00E396', pos, zero)
      ]
    },
    hoverInfo() {
      const i = this.hoverDay
      if (i === null || !this.dailySeries[i]) return null
      const d = this.dailySeries[i]
      return {
        index: i,
        x: this.xAt(i),
        rows: [
          { key: 'positive', label: 'Positive', color: '#00E396', value: d.positive, y: this.yAtCount(d.positive) },
          { key: 'neutral', label: 'Neutral', color: '#008FFB', value: d.neutral, y: this.yAtCount(d.positive + d.neutral) },
          { key: 'negative', label: 'Negative', color: '#FF4560', value: d.negative, y: this.yAtCount(d.positive + d.neutral + d.negative) }
        ]
      }
    },
    hoverMarkerRows() {
      const info = this.hoverInfo
      if (!info) return []
      if (!this.hoverOnEvent) return info.rows
      const ev = this.eventMarkers.find(m => m.index === info.index)
      if (!ev) return info.rows
      return info.rows.filter(r => Math.abs(r.y - ev.y) > 0.5)
    },
    eventMarkers() {
      return this.events
        .filter(ev => ev.index >= 0 && ev.index < this.timelineDays.length)
        .map(ev => {
          const x = this.xAt(ev.index)
          const day = this.dailySeries[ev.index]
          const total = day ? day.positive + day.neutral + day.negative : 0
          return {
            ...ev,
            x,
            y: this.yAtCount(total),
            leftPct: (x / 1000) * 100
          }
        })
    }
  },
  mounted() {
    const { start_date, end_date } = generateFilterDate(this.selectedPeriod);
    this.timelineRange = { start: start_date, end: end_date };
    this.loadGroupAnalysis();

    this.$nextTick(() => this.measureAudit());
    if (typeof ResizeObserver !== 'undefined' && this.$refs.echoColumn) {
      this.auditObserver = new ResizeObserver(() => this.measureAudit());
      this.auditObserver.observe(this.$refs.echoColumn);
      if (this.$refs.echoTopEl) this.auditObserver.observe(this.$refs.echoTopEl);
    }
    window.addEventListener('resize', this.measureAudit);
  },
  beforeDestroy() {
    this.stopAuditObserver();
  },
  beforeUnmount() {
    this.stopAuditObserver();
  },
  methods: {
    async loadAllPublicRelationData() {
      if (!this.selectedGroupAnalysisKey) return;

      trackEvent('strategic-reputation-positioning-apply-search', {
        title: `Strategic Reputation & Positioning Apply Search - ${this.selectedGroupAnalysisKey}`,
        groupAnalysis: this.selectedGroupAnalysisKey
      });

      this.messagePenetration = null;
      this.hoveredPillar = null;
      this.applyMessagePenetration(null);
      this.applyNarrativeWeather(null);
      this.applyHeadlineTimeline(null);

      const { start_date, end_date } = generateFilterDate(this.selectedPeriod);
      this.timelineRange = { start: start_date, end: end_date };

      this.isLoadingPublicRelationData = true;
      try {
        await Promise.all([
          this.loadMessagePenetration(),
          this.loadNarrativeWeather(),
          this.loadHeadlineTimeline(),
        ]);
      } finally {
        this.isLoadingPublicRelationData = false;
      }
    },
    async loadMessagePenetration() {
      try {
        const authToken = localStorage.getItem('token_app');
        if (!authToken) {
          throw new Error('Auth token not found');
        }

        const axiosInstance = axios.create({
          baseURL: '/apigenerativeAI',
          headers: { Authorization: `Bearer ${authToken}` },
        });

        const payload = {
          topic: this.selectedGroupAnalysisKey,
          period: this.selectedPeriod,
          group_analysis: this.selectedGroupAnalysisKey,
          match_threshold: 0.65,
          echo_threshold: 0.40,
          force_recreate: false,
        };

        const response = await axiosInstance.post(
          '/gen-ai-medan/api/v1/chat/describe-message-penetration',
          payload
        );

        let result = response?.data?.data ?? null;
        if (typeof result === 'string') result = JSON.parse(result);

        this.messagePenetration = result;
        this.applyMessagePenetration(result);
      } catch (err) {
        console.error('Error loadMessagePenetration:', err);
        this.messagePenetration = null;
        this.applyMessagePenetration(null);
      }
    },
    applyMessagePenetration(result) {
      const alignment = result?.slogan_alignment ?? [];

      this.pillars = alignment.map(item => ({
        name: item.slogan,
        score: Math.min(100, Math.max(0, Math.round(Number(item.penetration_score) || 0))),
      }));

      const wordMap = {};
      const addWord = (w, pillarIdx) => {
        if (!w?.text) return;
        const key = String(w.text).toLowerCase();
        if (!wordMap[key]) wordMap[key] = { key, text: key, count: 0, pillars: [], fromRisk: false };
        wordMap[key].count += Number(w.count) || 0;
        if (pillarIdx === null) wordMap[key].fromRisk = true;
        else if (!wordMap[key].pillars.includes(pillarIdx)) wordMap[key].pillars.push(pillarIdx);
      };

      alignment.forEach((item, i) => {
        (item.direct_hits || []).forEach(w => addWord(w, i));
        (item.diluted_echoes || []).forEach(w => addWord(w, i));
      });

      let risks = result?.unassigned_risks ?? [];
      if (typeof risks === 'string') {
        try {
          risks = JSON.parse(risks);
        } catch (e) {
          risks = [];
        }
      }
      const riskList = Array.isArray(risks) ? risks : [];
      [...riskList]
        .sort((a, b) => (Number(b?.count) || 0) - (Number(a?.count) || 0))
        .slice(0, maxUnassignedRisks)
        .forEach(w => addWord(w, null));

      const sumCount = arr => (Array.isArray(arr) ? arr : []).reduce((sum, w) => sum + (Number(w?.count) || 0), 0);
      const volumeOr = (vol, arr) => (vol !== undefined && vol !== null && !isNaN(Number(vol)) ? Number(vol) : sumCount(arr));

      const totalDiluted = alignment.reduce((sum, item) => sum + volumeOr(item.diluted_echoes_volume, item.diluted_echoes), 0);
      const totalDirect = alignment.reduce((sum, item) => sum + volumeOr(item.direct_hits_volume, item.direct_hits), 0);
      const totalUnassigned = Array.isArray(risks) && risks.length
        ? sumCount(risks)
        : volumeOr(alignment[0]?.unassigned_risks_volume, []);

      const matched = totalDiluted + totalDirect;
      const matchBase = matched + totalUnassigned;
      this.campaignMatch = matchBase > 0 ? Math.round((matched / matchBase) * 100) : 0;

      const words = Object.values(wordMap)
        .map(w => ({ ...w, unassigned: w.fromRisk && w.pillars.length === 0 }))
        .sort((a, b) => b.count - a.count);

      const pairRows = arr => {
        const sorted = [...arr].sort((x, y) => x.text.length - y.text.length);
        const rows = [];
        let i = 0;
        let j = sorted.length - 1;
        while (i <= j) {
          if (i === j) {
            rows.push([sorted[i]]);
            break;
          }
          const longest = sorted[j];
          const shortest = sorted[i];
          const tooLong =
            longest.text.length > maxPairWordLength ||
            longest.text.length + shortest.text.length > maxRowLength;
          if (tooLong) {
            rows.push([longest]);
            j--;
            continue;
          }
          rows.push([longest, shortest]);
          i++;
          j--;
        }
        return rows;
      };

      const shuffle = arr => {
        const a = [...arr];
        for (let i = a.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [a[i], a[j]] = [a[j], a[i]];
        }
        return a;
      };

      const rest = words.slice(1);
      let half = Math.ceil(rest.length / 2);
      if (half % 2 === 1 && half < rest.length) half += 1;

      const sloganWords = shuffle(rest.filter(w => !w.unassigned));
      const riskWords = shuffle(rest.filter(w => w.unassigned));

      let topSlogan = rest.length ? Math.round(sloganWords.length * half / rest.length) : 0;
      let topRisk = half - topSlogan;
      if (topRisk > riskWords.length) {
        topRisk = riskWords.length;
        topSlogan = half - topRisk;
      }
      if (topSlogan > sloganWords.length) {
        topSlogan = sloganWords.length;
        topRisk = half - topSlogan;
      }

      const topWords = shuffle([...sloganWords.slice(0, topSlogan), ...riskWords.slice(0, topRisk)]);
      const bottomWords = shuffle([...sloganWords.slice(topSlogan), ...riskWords.slice(topRisk)]);

      this.echoHighlight = words[0]?.text || '';
      this.echoHighlightKey = words[0]?.key || '';
      this.echoHighlightWord = words[0] || null;
      this.echoTop = pairRows(topWords);
      this.echoBottom = pairRows(bottomWords);

      this.$nextTick(() => this.measureAudit());
    },
    async loadNarrativeWeather() {
      try {
        const authToken = localStorage.getItem('token_app');
        if (!authToken) {
          throw new Error('Auth token not found');
        }

        const axiosInstance = axios.create({
          baseURL: '/apigenerativeAI',
          headers: { Authorization: `Bearer ${authToken}` },
        });

        const payload = {
          group_analysis: this.selectedGroupAnalysisKey,
          period: this.selectedPeriod,
        };

        const response = await axiosInstance.post(
          '/gen-ai-medan/api/v1/chat/describe-narrative-weather',
          payload
        );

        let result = response?.data?.data ?? null;
        if (typeof result === 'string') result = JSON.parse(result);

        this.applyNarrativeWeather(result);
      } catch (err) {
        console.error('Error loadNarrativeWeather:', err);
        this.applyNarrativeWeather(null);
      }
    },
    applyNarrativeWeather(result) {
      const items = result?.slogan_insight ?? [];

      this.forecasts = items.map(item => {
        const raw = String(item?.insight?.weather_forecast || '');
        const upper = raw.toUpperCase();

        let type = 'overcast';
        if (upper.includes('STORM')) type = 'storm';
        else if (upper.includes('CLEAR')) type = 'clear';

        return {
          type,
          label: raw.replace(/[^\x20-\x7E]/g, '').trim() || upper,
          theme: item.slogan,
          description: item?.insight?.summary || '',
          aiNote: item?.insight?.ai_note || '',
        };
      });
    },
    async loadHeadlineTimeline() {
      try {
        const authToken = localStorage.getItem('token_app');
        if (!authToken) {
          throw new Error('Auth token not found');
        }

        const axiosInstance = axios.create({
          baseURL: '/apigenerativeAI',
          headers: { Authorization: `Bearer ${authToken}` },
        });

        const payload = {
          group_analysis: this.selectedGroupAnalysisKey,
          start_date: this.timelineRange.start,
          end_date: this.timelineRange.end,
        };

        const response = await axiosInstance.post(
          '/gen-ai-medan/api/v1/chat/describe-headline-timeline',
          payload
        );

        let result = response?.data?.data ?? null;
        if (typeof result === 'string') result = JSON.parse(result);

        this.applyHeadlineTimeline(result);
      } catch (err) {
        console.error('Error loadHeadlineTimeline:', err);
        this.applyHeadlineTimeline(null);
      }
    },
    applyHeadlineTimeline(result) {
      const summary = result?.summary ?? [];

      this.timelineSummary = summary.filter(item => item?.date);

      this.events = [...summary]
        .filter(item => item?.date && item?.headline)
        .sort((a, b) => String(a.date).localeCompare(String(b.date)))
        .map((item, index) => ({
          index: this.dayIndex(item.date),
          text: item.headline,
          insight: item.headline_insight || '',
          tier: index % 2,
        }));
    },
    async loadGroupAnalysis() {
      try {
        const authToken = localStorage.getItem('token_app');
        if (!authToken) {
          throw new Error('Auth token not found');
        }

        const response = await axios.get('/public_relation/group_analysis', {
          headers: { 'auth-token': authToken },
        });

        const list = response?.data?.token?.data ?? [];
        this.itemGroupAnalysis = list;

        if (list.length > 0) {
          this.selectedGroupAnalysisKey = list[0].GROUP_ANALYSIS;
          this.loadAllPublicRelationData();
        }
      } catch (e) {
        this.itemGroupAnalysis = [];

        if (e.response) {
          const { status } = e.response;

          switch (status) {
            case 404:
              this.$refs.alert.error_login(404);
              break;
            case 403:
              this.$refs.alert.error_login(403);
              break;
            default:
              console.log('some other error');
              break;
          }
        } else {
          this.alert = true;
          setTimeout(() => {
            this.alert = false;
          }, 10000);
          this.message = 'API Public Relation Group Analysis Refused To Connect';
          this.colorAlert = 'red';
        }
      }
    },
    onChartMove(e) {
      const svg = this.$refs.timelineSvg;
      const wrap = this.$refs.timelineChart;
      const n = this.dailySeries.length;
      if (!svg || !wrap || !n) return;

      const r = svg.getBoundingClientRect();
      if (!r.width) return;
      const scale = 1000 / r.width;
      const sx = (e.clientX - r.left) * scale;
      const sy = (e.clientY - r.top) * scale;

      const { left, right, baseline } = this.chart;
      if (sx < left - 10 || sx > right + 10 || sy < 0 || sy > baseline + 4) {
        this.hoverDay = null;
        return;
      }

      const idx = n <= 1 ? 0 : Math.round(((sx - left) / (right - left)) * (n - 1));
      this.hoverDay = Math.min(n - 1, Math.max(0, idx));

      const c = wrap.getBoundingClientRect();
      const tipHeight = 96;
      const top = Math.min(Math.max(e.clientY - c.top - tipHeight / 2, 8), Math.max(8, c.height - tipHeight - 8));
      this.chartTip = {
        left: (this.xAt(this.hoverDay) / 1000) * c.width,
        top,
        flip: this.hoverDay > (n - 1) / 2,
      };
    },
    onChartLeave() {
      this.hoverDay = null;
      this.hoverOnEvent = false;
    },
    setHover(index) {
      this.measureAudit();
      this.hoveredPillar = index;
    },
    clearHover() {
      this.hoveredPillar = null;
    },
    measureAudit() {
      const col = this.$refs.echoColumn;
      const svg = this.$refs.connectorsEl;
      const list = this.$refs.pillarList;
      if (!col || !svg) return;

      const h = Math.round(col.offsetHeight);
      if (h && h !== this.auditHeight) this.auditHeight = h;

      const s = svg.getBoundingClientRect();

      this.pillarCenters = list
        ? Array.from(list.querySelectorAll('.pillar-card')).map(el => {
            const r = el.getBoundingClientRect();
            return Math.round(r.top - s.top + r.height / 2);
          })
        : [];

      this.wordTargets = Array.from(col.querySelectorAll('[data-key]')).map(el => {
        const r = el.getBoundingClientRect();
        const key = el.getAttribute('data-key');
        const word = this.allEchoWords.find(w => w.key === key);
        return {
          key,
          x: Math.round(r.left - s.left - 6),
          y: Math.round(r.top - s.top + r.height / 2),
          related: this.relatedPillarsOf(word),
          unassigned: !!word?.unassigned,
        };
      });
    },
    relatedPillarsOf(word) {
      if (!word) return [];
      return word.pillars || [];
    },
    stopAuditObserver() {
      window.removeEventListener('resize', this.measureAudit);
      if (this.auditObserver) {
        this.auditObserver.disconnect();
        this.auditObserver = null;
      }
    },
    connectorPath(index, target) {
      const cy = this.pillarCenters[index] ?? (36 + index * 90);
      const half = target.x / 2;
      return `M 0 ${cy} C ${half} ${cy}, ${half} ${target.y}, ${target.x} ${target.y}`;
    },
    wordClass(word) {
      if (this.hoveredPillar === null) return {};
      const related = this.relatedPillarsOf(word).includes(this.hoveredPillar);
      return { 'is-related': related, 'is-dim': !related };
    },
    wordStyle(word) {
      const range = this.echoCountRange;
      if (!range || !word) return {};
      const minSize = 13;
      const maxSize = 20;
      const ratio = range.max === range.min ? 1 : (word.count - range.min) / (range.max - range.min);
      const size = minSize + Math.sqrt(Math.max(0, Math.min(1, ratio))) * (maxSize - minSize);
      return { fontSize: size.toFixed(1) + 'px' };
    },
    xAt(index) {
      const { left, right } = this.chart;
      const n = this.timelineDays.length;
      if (n <= 1) return (left + right) / 2;
      return left + (index / (n - 1)) * (right - left);
    },
    yAtCount(value) {
      const { top, baseline } = this.chart;
      const ratio = Math.min(1, Math.max(0, value / (this.timelineYMax || 1)));
      return baseline - ratio * (baseline - top);
    },
    monotonePath(pts) {
      const n = pts.length;
      if (!n) return '';
      if (n === 1) return `M ${pts[0].x} ${pts[0].y}`;

      const dx = [];
      const slope = [];
      for (let i = 0; i < n - 1; i++) {
        dx[i] = pts[i + 1].x - pts[i].x;
        slope[i] = (pts[i + 1].y - pts[i].y) / dx[i];
      }

      const tan = new Array(n);
      tan[0] = slope[0];
      tan[n - 1] = slope[n - 2];
      for (let i = 1; i < n - 1; i++) {
        tan[i] = slope[i - 1] * slope[i] <= 0 ? 0 : (slope[i - 1] + slope[i]) / 2;
      }
      for (let i = 0; i < n - 1; i++) {
        if (slope[i] === 0) {
          tan[i] = 0;
          tan[i + 1] = 0;
          continue;
        }
        const a = tan[i] / slope[i];
        const b = tan[i + 1] / slope[i];
        const s = a * a + b * b;
        if (s > 9) {
          const k = 3 / Math.sqrt(s);
          tan[i] = k * a * slope[i];
          tan[i + 1] = k * b * slope[i];
        }
      }

      let d = `M ${pts[0].x} ${pts[0].y}`;
      for (let i = 0; i < n - 1; i++) {
        const h = dx[i] / 3;
        d +=
          ` C ${pts[i].x + h} ${pts[i].y + tan[i] * h},` +
          ` ${pts[i + 1].x - h} ${pts[i + 1].y - tan[i + 1] * h},` +
          ` ${pts[i + 1].x} ${pts[i + 1].y}`;
      }
      return d;
    },
    closeArea(path, pts) {
      if (!pts.length) return '';
      const last = pts[pts.length - 1];
      const first = pts[0];
      return `${path} L ${last.x} ${this.chart.baseline} L ${first.x} ${this.chart.baseline} Z`;
    },
    parseDate(str) {
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(str || ''));
      return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
    },
    dayIndex(dateStr) {
      const t = this.parseDate(dateStr);
      const s = this.parseDate(this.timelineRange.start);
      return t === null || s === null ? -1 : Math.round((t - s) / 86400000);
    },
    formatDay(t) {
      const d = new Date(t);
      return `${d.getUTCDate()} ${monthsId[d.getUTCMonth()]}`;
    },
    openEventModal(ev) {
      const t = this.timelineDays[ev.index];
      const year = new Date(t).getUTCFullYear();
      const day = this.dailySeries[ev.index];
      this.selectedEvent = {
        title: ev.text,
        date: `${this.formatDay(t)} ${year}`,
        insight: ev.insight,
        sentiment: day
          ? { positive: day.positive, neutral: day.neutral, negative: day.negative }
          : null,
      };
    },
    closeEventModal() {
      this.selectedEvent = null;
    },
  },
}
</script>

<style scoped>
.narrative-climate-wrapper {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  padding: 16px;
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.page-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.page-title {
  font-size: 20px;
  font-weight: 700;
  color: #000;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.header-select-wrapper {
  position: relative;
  width: 195px;
  flex-shrink: 0;
}

.header-select-wrapper--period {
  width: 190px;
}

.header-group-select {
  background-color: white;
  cursor: pointer;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  color: #333;
  font-size: 14px;
  height: 47px;
  width: 100%;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px 40px 12px 16px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.header-group-select:focus {
  border-color: #0471A5;
  box-shadow: 0 0 0 2px rgba(4, 113, 165, 0.15);
}

.header-chevron-icon {
  position: absolute;
  pointer-events: none;
  color: #666;
  top: 50%;
  right: 14px;
  transform: translateY(-50%);
}

.header-apply-btn {
  background-color: #000 !important;
  color: white !important;
  height: 47px !important;
  min-width: 0 !important;
  border-radius: 8px !important;
  font-size: 17px !important;
  font-weight: 500 !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  box-shadow: none !important;
  flex-shrink: 0;
  padding: 0 24px !important;
}

.header-apply-btn :deep(.v-btn__content) {
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  height: 100%;
}

.header-apply-btn :deep(.v-icon) {
  margin-top: 0;
  margin-bottom: 0;
  line-height: 1;
  vertical-align: middle;
}

.headline-banner {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
  border: 1px solid #cbd9e8;
  border-radius: 8px;
  background: linear-gradient(90deg, #dbe7f3 0%, #eef3f8 100%);
  margin-bottom: 16px;
}

.headline-text {
  margin: 0;
  font-size: 16px;
  line-height: 1.5;
  color: #0f172a;
}

.headline-text strong {
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.2px;
}

.pr-card {
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 20px 22px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
}

.audit-card {
  overflow: hidden;
}

.card-header-text {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.card-title {
  font-size: 17px;
  font-weight: 700;
  color: #000000;
  margin: 0;
  letter-spacing: 0.3px;
}

.card-subtitle {
  font-size: 16px;
  color: #475569;
  margin: 0;
}

.key-drivers-label {
  font-size: 12px;
  color: #000;
  margin: 0;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}

.audit-forecast-row {
  display: grid;
  grid-template-columns: 1.15fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.audit-body {
  display: grid;
  grid-template-columns: minmax(180px, max-content) 80px minmax(0, 1fr);
  grid-template-rows: auto auto;
  row-gap: 16px;
  align-items: stretch;
  position: relative;
}

.col-head {
  align-self: start;
}

.col-head--left {
  grid-column: 1;
  grid-row: 1;
}

.col-head--right {
  grid-column: 3;
  grid-row: 1;
  text-align: center;
}

.col-head-title {
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  line-height: 1.2;
  color: #0f172a;
  letter-spacing: 0.2px;
}

.col-head-sub {
  margin: 2px 0 0 0;
  font-size: 13.5px;
  line-height: 1.3;
  color: #4b5563;
}

.audit-divider {
  grid-column: 2;
  grid-row: 1 / span 2;
  justify-self: center;
  width: 0;
  border-left: 2px dashed #c3cad3;
  z-index: 0;
}

.pillar-list {
  grid-column: 1;
  grid-row: 2;
  align-self: center;
}

.audit-connectors {
  grid-column: 2;
  grid-row: 2;
  position: relative;
  z-index: 1;
}

.echo-column {
  grid-column: 3;
  grid-row: 2;
}

.pillar-name::before {
  content: '[';
}

.pillar-name::after {
  content: ']';
}

.pillar-list {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.pillar-card {
  height: 72px;
  box-sizing: border-box;
  padding: 10px 14px;
  border: 1px solid #cfe9dc;
  border-radius: 12px;
  background: #f1faf5;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  outline: none;
  transition: opacity 0.2s, border-color 0.2s, box-shadow 0.2s, transform 0.2s;
}

.pillar-card--active,
.pillar-card:focus-visible {
  border-color: #22c55e;
  box-shadow: 0 4px 14px rgba(34, 197, 94, 0.25);
  transform: translateX(2px);
}

.pillar-card--dim {
  opacity: 0.6;
}

.pillar-name {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: #111;
  text-transform: uppercase;
  line-height: 1.25;
  white-space: nowrap;
}

.pillar-bar-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.pillar-track {
  flex: 1;
  height: 8px;
  border-radius: 999px;
  background: #d9e2ea;
  overflow: hidden;
}

.pillar-fill {
  height: 100%;
  border-radius: 999px;
  background: #22c55e;
}

.pillar-score {
  font-size: 14px;
  font-weight: 700;
  color: #111;
  min-width: 36px;
  text-align: right;
}

.audit-connectors {
  width: 100%;
  height: 252px;
  display: block;
  overflow: visible;
  pointer-events: none;
}

.connector-line {
  stroke-dasharray: 600;
  stroke-dashoffset: 600;
  animation: draw-line 0.35s ease forwards;
}

@keyframes draw-line {
  to {
    stroke-dashoffset: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .connector-line {
    animation: none;
    stroke-dashoffset: 0;
  }
}

.echo-column {
  align-self: start;
  min-height: 252px;
  display: grid;
  grid-template-rows: auto auto auto;
  align-content: start;
  row-gap: 10px;
  justify-items: center;
  text-align: center;
  padding: 0;
  margin: 0 -14px 0 -24px;
}

.echo-top {
  align-self: start;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.echo-bottom {
  align-self: start;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.echo-row {
  display: flex;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: baseline;
  gap: 4px 10px;
}

.echo-word {
  font-size: 15px;
  font-weight: 500;
  line-height: 1.25;
  color: #1f2937;
  letter-spacing: 0.1px;
  white-space: nowrap;
  transition: color 0.2s, opacity 0.2s;
}

.echo-word::before {
  content: '[';
}

.echo-word::after {
  content: ']';
}

.echo-highlight {
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 31px;
  font-weight: 800;
  line-height: 1.12;
  color: #4caf6a;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  text-shadow: 0 1px 0 rgba(76, 175, 106, 0.15);
  transition: opacity 0.2s;
}

.echo-highlight-line {
  display: block;
  white-space: nowrap;
}

.echo-highlight-line:first-child::before {
  content: '[';
}

.echo-highlight-line:last-child::after {
  content: ']';
}

.echo-word.is-related {
  color: #16a34a;
}

.echo-word.is-dim,
.echo-highlight.is-dim {
  opacity: 0.3;
}

.echo-column {
  position: relative;
  z-index: 2;
}

.match-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 22px;
  margin: 20px 8px;
  padding: 0 8px;
}

.match-gauge {
  width: 128px;
  flex-shrink: 0;
  display: block;
}

.match-text {
  margin: 0;
  display: flex;
  flex-direction: column;
  font-size: 15px;
  font-weight: 800;
  line-height: 1.2;
  color: #0f172a;
  text-transform: uppercase;
  letter-spacing: 0.2px;
  gap: 2px;
}

.match-arrow {
  flex-shrink: 0;
  color: #4caf6a;
  margin-left: 4px;
}

.match-arrow--down {
  color: #FF4560;
  transform: rotate(90deg);
}

.forecast-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex: 1;
}

.forecast-item {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  padding: 14px 16px;
  border: 1px solid #e2e8f0;
  border-left-width: 4px;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.05);
}

.forecast-item--clear {
  background: #f3fbf6;
  border-left-color: #22c55e;
}

.forecast-item--overcast {
  background: #f4f7fa;
  border-left-color: #008FFB;
}

.forecast-item--storm {
  background: #fef2f2;
  border-left-color: #FF4560;
}

.forecast-icon {
  flex-shrink: 0;
  width: 56px;
  display: flex;
  justify-content: center;
  padding-top: 2px;
}

.forecast-body {
  min-width: 0;
}

.forecast-title {
  margin: 0 0 4px 0;
  font-size: 14px;
  font-weight: 800;
  color: #000;
  letter-spacing: 0.2px;
}

.forecast-icon--inline {
  display: inline-flex;
  width: auto;
  padding-top: 0;
  margin-right: 2px;
  vertical-align: middle;
}

.forecast-icon--inline svg {
  width: 22px;
  height: auto;
}

.forecast-theme {
  font-weight: 800;
  color: #000;
  text-transform: uppercase;
}

.forecast-theme::before {
  content: '| Theme: [';
}

.forecast-theme::after {
  content: ']';
}

.forecast-description {
  margin: 0 0 6px 0;
  font-size: 13.5px;
  line-height: 1.55;
  color: #000;
}

.forecast-note {
  margin: 0;
  font-size: 13.5px;
  line-height: 1.55;
  color: #000;
}

.forecast-note strong {
  font-weight: 800;
  text-transform: uppercase;
  font-size: 12.5px;
  letter-spacing: 0.3px;
}

.timeline-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
}

.timeline-legend {
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
  font-size: 12.5px;
  color: #334155;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.legend-swatch {
  width: 12px;
  height: 12px;
  border-radius: 3px;
}

.legend-swatch--positive {
  background: #00E396;
}

.legend-swatch--neutral {
  background: #008FFB;
}

.legend-swatch--negative {
  background: #FF4560;
}

.timeline-scroll {
  overflow-x: auto;
}

.timeline-chart {
  position: relative;
  min-width: 760px;
}

.event-band {
  position: relative;
  height: 112px;
}

.event-label {
  position: absolute;
  bottom: 4px;
  transform: translateX(-50%);
  width: 104px;
  text-align: center;
  font-size: 11.5px;
  font-weight: 600;
  line-height: 1.3;
  color: #0f172a;
}

.event-label--high {
  bottom: 56px;
}

.event-label--high::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 100%;
  height: 52px;
  border-left: 1px dashed #94a3b8;
}

.timeline-svg {
  width: 100%;
  height: auto;
  display: block;
}

.axis-text {
  font-size: 12px;
  fill: #64748b;
}

.chart-tooltip {
  position: absolute;
  z-index: 5;
  min-width: 118px;
  padding: 10px 14px;
  background: #ffffff;
  border-radius: 10px;
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.18);
  display: flex;
  flex-direction: column;
  gap: 8px;
  pointer-events: none;
  transform: translateX(14px);
}

.chart-tooltip--left {
  transform: translateX(calc(-100% - 14px));
}

.chart-tooltip-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12.5px;
  color: #0f172a;
  white-space: nowrap;
}

.chart-tooltip-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.chart-tooltip-label {
  flex: 1;
}

.chart-tooltip-value {
  font-weight: 800;
}

.event-label--clickable {
  cursor: pointer;
}

.event-label--clickable:hover {
  text-decoration: underline;
}

.event-dot {
  cursor: pointer;
}

.event-dot:hover .event-dot-point {
  fill: #ffffff;
}

.portal-modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.portal-modal {
  background-color: #ffffff;
  border-radius: 10px;
  padding: 24px;
  width: 100%;
  max-width: 520px;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0,0,0,0.2);
  position: relative;
}

.portal-modal-close {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid #e2e8f0;
  background-color: #f8fafc;
  color: #475569;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.portal-modal-close:hover {
  background-color: #f1f5f9;
}

.portal-modal-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
  padding-right: 24px;
}

.portal-modal-title {
  font-size: 16px;
  font-weight: 700;
  color: #000000;
  margin: 0;
  text-transform: uppercase;
}

.portal-modal-subtitle {
  font-size: 13px;
  color: #334155;
}

.portal-modal-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 18px;
  margin-top: 4px;
}

.portal-modal-sentiment {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 16px;
  font-size: 13px;
  color: #334155;
}

.sentiment-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.sentiment-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.sentiment-dot--positive {
  background: #00E396;
}

.sentiment-dot--neutral {
  background: #008FFB;
}

.sentiment-dot--negative {
  background: #FF4560;
}

.portal-modal-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.portal-modal-block p {
  font-size: 13.5px;
  line-height: 1.55;
  color: #334155;
  margin: 0;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s;
}

.modal-fade-enter,
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

@media (max-width: 1100px) {
  .audit-forecast-row {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .audit-body {
    grid-template-columns: 1fr;
    grid-template-rows: none;
  }
  .col-head--left,
  .col-head--right,
  .pillar-list,
  .echo-column {
    grid-column: 1;
    grid-row: auto;
    text-align: left;
  }
  .audit-connectors,
  .audit-divider {
    display: none;
  }
  .echo-column {
    height: auto;
    gap: 10px;
    margin: 14px 0 0;
  }
  .page-title {
    font-size: 17px;
  }
}

.header-setting-btn {
  background-color: #0471A5 !important;
  color: white !important;
  height: 45px !important;
  border-radius: 8px !important;
  font-size: 17px !important;
  font-weight: 500 !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  box-shadow: none !important;
  flex-shrink: 0;
  padding: 12px 24px !important;
}
</style>